import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons"

import { JElement } from '../../app/types/catalog-types';
import { useEnvConfig } from '../../app/context/environment-context';
import { useAppSelector } from '../../app/hooks';
import { catalogSelector } from '../../app/configurator-selectors';
import { TabList } from '../../controls/tabList/tab-list';

import styles from './element-chooser.module.css';
import { clipPlaneFragment } from '@babylonjs/core/Shaders/ShadersInclude/clipPlaneFragment';

export function ElementChooser({ className, onClose, onSelectElement, profile }: {
    className?: string
    onClose: () => void
    onSelectElement: (elementId: string) => void
    profile?: string
}) {
    const { elementGroups, elementFilter, elements, attachProfiles } = useAppSelector(catalogSelector);
    const egs = elementGroups ?? [];
    const ef =  elementFilter ?? [];
    //console.log(ef);
    const [elementHidden, setElementHidden] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    // Set the default value to ef[0].id if ef has elements, otherwise use 'none' a fallback
    const [sizeFilter, setSizeFilter] = useState<string>(ef.length > 1 ? ef[0].id : 'none');

    const compatibleProfiles = (attachProfiles != undefined && profile != undefined) ? 
        attachProfiles[profile] ?? [] 
        :
        [];

    const [availableFilters, setAvailableFilters] = useState<string[]>([]);

    useEffect(() => {
        const filters = new Set<string>();
        
        elements.filter(e => {
            if(profile) {
                if(!e.attach) {
                    return false;
                }
                return e.attach.some(a => compatibleProfiles.includes(a.profile));
            }
            return true;
        }).forEach(e => {
            if (e.filter && e.filter.length > 0) {
                e.filter.forEach(f => filters.add(f));
            }
        });

        if (filters.size <= 1) {
            setSizeFilter('none');
        }
    
        setAvailableFilters(Array.from(filters));
    }, [elements]); // Update whenever `elements` change

    const groups: [ JElement[], JElement[] ][] = useMemo(() => {
        return egs.map(eg => {
            const groupElements = elements.filter(e => {
                if(!e.group) {
                    return false;
                } else if(e.group[0] !== eg.id) { //filter elements that are not in the current group
                    return false;
                } else if(profile) { //filter elements that are not compatible with the current profile  
                    if(!e.attach) {
                        return false;
                    } else {
                        return e.attach.some(a => compatibleProfiles.includes(a.profile));
                    }
                } else {
                    return true;
                    // code to be added to implement the filter                                                  
                } 
                
            })
                        
            const groupElementsFiltered = groupElements.filter(e => {
                if (sizeFilter !== 'none') {
                    if(!e.filter || e.filter.length === 0) { //if elements has nog filter, always show
                        return true;
                    }
                    return e.filter.includes(sizeFilter); //if element has filter, check if it is in the filter list
                }
                return true;
            });

            groupElementsFiltered.sort((a, b) => {
                return a.group![1] - b.group![1]
            });
            const splitIndex = groupElementsFiltered.length % 2 === 0 ? groupElementsFiltered.length / 2 
                                                              : (groupElementsFiltered.length + 1) / 2;
            return [ groupElementsFiltered.slice(0, splitIndex), groupElementsFiltered.slice(splitIndex) ];
        });
    }, [egs, profile, sizeFilter]);

    const tabElements = () => {
        return elementGroups?.filter((_, idx) => groupLength(groups[idx]) > 0)
    }

    const onHandleClose = () => {
        setElementHidden(true);
        setTimeout(function () {
            onClose();
        }, 700);
    }

    return (
        <div className={`${styles.elementChooser} ${elementHidden ? styles.elementHidden : ''} ${className ?? ''}`}>
            <TabList 
                scrollable={true} 
                elements={tabElements()}
                onSelectTab={setTabIndex}
                selectedTab={tabIndex}
                availableFilters={availableFilters} 
                ef={ef} 
                sizeFilter={sizeFilter} 
                setSizeFilter={setSizeFilter} 
            />
            <div className={styles.mainContainer}>
                {
                    groups.filter(group => groupLength(group) > 0).map((group, idx) => (
                        <ElementMenu 
                            key={idx} 
                            elementRows={group} 
                            hidden={idx !== tabIndex}
                            onSelectElement={onSelectElement}
                        />
                    ))
                }
                <FontAwesomeIcon
                    icon={faXmark}
                    className={styles.closeButton}
                    onClick={onHandleClose}
                />
            </div>
        </div>
    );


}

function groupLength(group: JElement[][]) {
    //return group.reduce((acc, row) => acc + row.length, 0);
    if (!group) return 0; // Handle undefined
    return group[0].length + group[1].length;
}

export function ElementMenu({ hidden, elementRows, onSelectElement }: {
    hidden?: boolean
    elementRows: JElement[][]
    onSelectElement: (elementId: string) => void
}) {
    const maxCols = elementRows.reduce((max, row) => Math.max(max, row.length), 0);
    const { thumbnailsPath } = useEnvConfig();
    return (
        <ul className={`${styles.elementMenu} ${hidden ? styles.hidden : ''}`} role="tabpanel"
            style={{ gridTemplateColumns: `repeat(${maxCols},minmax(160px,240px)) 1fr`}}>
        {
            elementRows.map((row, rowIdx) => {
                return row.map((element, colIdx) => {
                    return <li key={element.id} style={{gridRow: rowIdx+1, gridColumn: colIdx+1}}>
                        <button type="button" className={styles.elementBtn} onClick={() => onSelectElement(element.id)}>
                            <img src={element?.thumbURL ?? `${thumbnailsPath}/elements/${element.id}.jpg` } alt="" />
                            <span>{element.name}</span>
                        </button>
                    </li>
                })
            })
        }
        </ul>
    );
}
